import { useState } from "react";
import { base44 } from "@/api/base44Client";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminGuard from "@/components/admin/AdminGuard";
import { useApp } from "@/lib/AppContext";
import { t } from "@/lib/translations";

export default function AdminVerify() {
  const [query, setQuery] = useState("");
  const [queryType, setQueryType] = useState("code");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [success, setSuccess] = useState("");
  const { lang } = useApp();
  const tr = t[lang];
  const locale = lang === "ar" ? "ar-SA" : "en-US";

  const statusConfig = {
    unused: { valid: true, label: tr.couponValid },
    used: { valid: false, label: tr.couponUsed },
    expired: { valid: false, label: tr.couponExpired },
    cancelled: { valid: false, label: tr.couponCancelled },
    rejected: { valid: false, label: tr.couponCancelled },
    pending: { valid: false, label: tr.pending },
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    setSuccess("");

    let coupons = [];
    if (queryType === "code") {
      coupons = await base44.entities.Coupon.filter({ code: query.trim().toUpperCase() });
    } else {
      coupons = await base44.entities.Coupon.filter({ customer_phone: query.trim(), status: "unused" });
    }

    if (coupons.length === 0) {
      setResult({ found: false });
    } else {
      const coupon = coupons[0];
      const offer = await base44.entities.Offer.filter({ id: coupon.offer_id });

      const now = new Date();
      const isExpired = coupon.expires_at && new Date(coupon.expires_at) < now;
      if (isExpired && coupon.status === "unused") {
        await base44.entities.Coupon.update(coupon.id, { status: "expired" });
        coupon.status = "expired";
      }

      setResult({ found: true, coupon, offer: offer[0] || null });
    }
    setLoading(false);
  };

  const handleApply = async () => {
    if (!result?.coupon) return;
    setApplying(true);

    const session = JSON.parse(sessionStorage.getItem("admin_session") || "{}");
    await base44.entities.Coupon.update(result.coupon.id, {
      status: "used",
      used_at: new Date().toISOString(),
      used_by_admin: session.username || (lang === "ar" ? "مدير النظام" : "System Admin")
    });

    const customers = await base44.entities.Customer.filter({ phone: result.coupon.customer_phone });
    if (customers[0]) {
      await base44.entities.Customer.update(customers[0].id, {
        total_coupons_used: (customers[0].total_coupons_used || 0) + 1,
        total_coupons_unused: Math.max(0, (customers[0].total_coupons_unused || 0) - 1)
      });
    }

    setResult(prev => ({ ...prev, coupon: { ...prev.coupon, status: "used" } }));
    setSuccess(`✅ ${lang === "ar" ? "تم تطبيق الكوبون بنجاح!" : "Coupon applied successfully!"}`);
    setApplying(false);
  };

  const statusInfo = result?.coupon ? statusConfig[result.coupon.status] : null;

  return (
    <AdminGuard>
      <AdminLayout>
        <div className="max-w-2xl mx-auto space-y-6">
          <div>
            <h1 className="text-2xl font-black text-foreground">✅ {tr.verifyTitle}</h1>
            <p className="text-muted-foreground text-sm">
              {lang === "ar" ? "ادخل كود الكوبون أو رقم جوال العميل للتحقق" : "Enter coupon code or customer phone to verify"}
            </p>
          </div>

          {/* Search Form */}
          <div className="bg-card rounded-2xl p-6 border border-border shadow-sm">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setQueryType("code")}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-colors ${queryType === "code" ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground hover:bg-secondary/80"}`}
                >
                  🎫 {lang === "ar" ? "بحث بالكود" : "Search by Code"}
                </button>
                <button
                  type="button"
                  onClick={() => setQueryType("phone")}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-colors ${queryType === "phone" ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground hover:bg-secondary/80"}`}
                >
                  📱 {lang === "ar" ? "بحث برقم الجوال" : "Search by Phone"}
                </button>
              </div>

              <div className="flex gap-3">
                <input
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder={queryType === "code"
                    ? (lang === "ar" ? "أدخل كود الكوبون مثل: DSC-A8K3" : "Enter coupon code e.g. DSC-A8K3")
                    : (lang === "ar" ? "أدخل رقم الجوال: 05xxxxxxxx" : "Enter phone: 05xxxxxxxx")}
                  className="flex-1 px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary text-sm bg-background text-foreground"
                  dir={queryType === "code" ? "ltr" : "auto"}
                />
                <button
                  type="submit"
                  disabled={loading || !query.trim()}
                  className="px-6 py-3 bg-primary text-primary-foreground font-bold rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-60"
                >
                  {loading ? "⌛" : `🔍 ${tr.verify}`}
                </button>
              </div>
            </form>
          </div>

          {/* Results */}
          {result && (
            <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
              {!result.found ? (
                <div className="p-8 text-center">
                  <div className="text-5xl mb-3">❌</div>
                  <h3 className="text-lg font-bold text-foreground">{tr.couponNotFound}</h3>
                  <p className="text-muted-foreground text-sm mt-1">
                    {lang === "ar" ? "تأكد من صحة البيانات المدخلة" : "Please check the entered data"}
                  </p>
                </div>
              ) : (
                <div>
                  <div className={`p-5 flex items-center gap-4 ${statusInfo?.valid ? "bg-green-50 dark:bg-green-950/30 border-b border-green-100 dark:border-green-900" : "bg-red-50 dark:bg-red-950/30 border-b border-red-100 dark:border-red-900"}`}>
                    <div className="text-4xl">{statusInfo?.valid ? "✅" : "❌"}</div>
                    <div>
                      <h3 className={`text-xl font-black ${statusInfo?.valid ? "text-green-700 dark:text-green-400" : "text-red-700 dark:text-red-400"}`}>
                        {statusInfo?.label}
                      </h3>
                      <p className={`text-sm ${statusInfo?.valid ? "text-green-600 dark:text-green-500" : "text-red-600 dark:text-red-500"}`}>
                        {lang === "ar" ? "الحالة:" : "Status:"} {statusInfo?.label}
                      </p>
                    </div>
                  </div>

                  <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <InfoRow label={lang === "ar" ? "كود الكوبون" : "Coupon Code"} value={<code className="font-mono font-bold text-primary">{result.coupon.code}</code>} />
                      <InfoRow label={lang === "ar" ? "رقم الجوال" : "Phone"} value={result.coupon.customer_phone} />
                      <InfoRow label={lang === "ar" ? "اسم العرض" : "Offer"} value={result.coupon.offer_title || result.offer?.title || "—"} />
                      <InfoRow label={lang === "ar" ? "تاريخ الانتهاء" : "Expires"} value={result.coupon.expires_at ? new Date(result.coupon.expires_at).toLocaleDateString(locale) : "—"} />
                      {result.coupon.used_at && (
                        <InfoRow label={lang === "ar" ? "تاريخ الاستخدام" : "Used At"} value={new Date(result.coupon.used_at).toLocaleDateString(locale)} />
                      )}
                      {result.coupon.used_by_admin && (
                        <InfoRow label={lang === "ar" ? "طُبِّق بواسطة" : "Applied By"} value={result.coupon.used_by_admin} />
                      )}
                    </div>

                    {result.offer?.terms && (
                      <div className="bg-amber-50 dark:bg-amber-950/30 rounded-xl p-4">
                        <p className="text-sm text-amber-800 dark:text-amber-300">
                          <strong>📋 {lang === "ar" ? "شروط العرض:" : "Offer Terms:"}</strong> {(lang === "en" && result.offer.terms_en) ? result.offer.terms_en : result.offer.terms}
                        </p>
                      </div>
                    )}

                    {result.coupon.admin_note && (
                      <div className="bg-blue-50 dark:bg-blue-950/30 rounded-xl p-4">
                        <p className="text-sm text-blue-800 dark:text-blue-300">
                          <strong>📝 {lang === "ar" ? "ملاحظة:" : "Note:"}</strong> {result.coupon.admin_note}
                        </p>
                      </div>
                    )}

                    {statusInfo?.valid && !success && (
                      <button
                        onClick={handleApply}
                        disabled={applying}
                        className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 text-white text-lg font-black rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-60"
                      >
                        {applying ? `⌛ ${lang === "ar" ? "جاري التطبيق..." : "Applying..."}` : `✅ ${tr.applyCoupon}`}
                      </button>
                    )}

                    {success && (
                      <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-xl p-4 text-center">
                        <p className="text-green-800 dark:text-green-400 font-bold">{success}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </AdminLayout>
    </AdminGuard>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground font-medium">{label}</span>
      <span className="text-sm font-bold text-foreground">{value}</span>
    </div>
  );
}